import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Loader2, MapPin, Stamp, TrendingUp, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Owner analytics dashboard. Reads GET /analytics/owner which returns
// authoritative counts (from count_documents / distinct) plus a slice of
// the 10 most recent check-ins across all owned/staffed campsites.
export default function OwnerAnalytics() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/analytics/owner")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load analytics."); });
    return () => { cancelled = true; };
  }, [api]);

  if (error) return <div className="card-elev p-6" style={{ color: "var(--premium)" }}>{error}</div>;
  if (!data) return (
    <div className="max-w-3xl mx-auto py-12 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
    </div>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="owner-analytics-page">
      <div>
        <Link to="/owner" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="owner-analytics-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Owner
        </Link>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ color: "var(--on-surface)" }}>Analytics</h1>
        <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
          Real-time counters across every campsite you own or manage.
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Kpi icon={MapPin}  label="Campsites"       value={data.campsites_count ?? data.campsites ?? 0} />
        <Kpi icon={Stamp}   label="Stamps issued"   value={data.stamps_issued ?? 0} accent="premium" />
        <Kpi icon={Clock}   label="Pending stamps"  value={data.pending ?? 0} />
        <Kpi icon={Users}   label="Unique visitors" value={data.unique_visitors ?? data.unique ?? 0} />
      </div>

      {/* Recent activity */}
      <section>
        <h2 className="text-lg font-extrabold mb-3" style={{ color: "var(--on-surface)" }}>Recent activity</h2>
        {(data.recent || []).length === 0 ? (
          <div className="card-elev p-6 text-sm" style={{ color: "var(--on-surface-muted)" }} data-testid="analytics-empty">
            No recent check-ins yet. As campers stamp in, they'll show up here.
          </div>
        ) : (
          <div className="card-elev overflow-hidden divide-y" style={{ borderColor: "var(--border-c)" }}>
            {(data.recent || []).map((c) => <RecentRow key={c.checkin_id || c.id} c={c} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "brand" }) {
  return (
    <div className="card-elev p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest"
           style={{ color: "var(--on-surface-muted)" }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl md:text-3xl font-extrabold" style={{ color: `var(--${accent})` }}>
        {Number(value).toLocaleString()}
      </div>
    </div>
  );
}

function RecentRow({ c }) {
  const status = c.status || "pending";
  const statusColor = status === "validated" ? "var(--brand)" : status === "pending" ? "var(--premium)" : "#B45309";
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`analytics-row-${c.checkin_id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-extrabold shrink-0"
           style={{ background: "var(--brand)", color: "white" }}>
        {(c.camper_name?.[0] || c.camper_display_name?.[0] || "C").toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>
          {c.camper_name || c.camper_display_name || "Camper"}
        </div>
        <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>
          {c.campsite_name || "—"} · {(c.checkin_date || c.created_at || "").slice(0, 10)}
        </div>
      </div>
      <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0"
            style={{ background: "color-mix(in srgb, currentColor 15%, transparent)", color: statusColor }}>
        {status}
      </span>
    </div>
  );
}
